import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RemoveIcon from '@mui/icons-material/Remove';
import { yupResolver } from '@hookform/resolvers/yup';
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { Controller, useFieldArray, useForm, useFormContext, useWatch } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router';
import { array, number, object, string } from 'yup';
import { organizationApi } from 'src/api/organizationApi';
import { permissionsApi } from 'src/api/permissions';
import { pimApi } from 'src/api/pimApi';
import {
  parseLicensesPageFromResponse,
  parseSubscriptionFromResponse,
  subscriptionsApi,
  type CreateSubscriptionPayload,
  type SubscriptionEntityType,
  type SubscriptionLicense,
} from 'src/api/subscriptionsApi';
import { Button, Tabs } from 'src/common';
import { RHFCustomSelect, RHFProvider, RHFTextField } from 'src/common/rhf';
import SubHeader from 'src/components/SubHeader';
import { QuickCreateSubscription, SubscriptionLicensesTable } from './create-form';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import {
  mapActivePermissions,
  resolveIncludedExcludedModules,
  type ActivePermission,
} from './productPermissionModules';
import { resolveCatalogDomainLabel } from 'src/mindtime/config/subscriptionPermissionRegistry';

const MAX_PRODUCT_ROWS = 10;

type SubscriptionType = 'monthly' | 'yearly';

type SelectOption = { id: string; label: string; value: string };

type PackageIncludedProduct = {
  id: string;
  name: string;
  solution?: string;
  permissionIds: string[];
};

type MsrpKind = 'monthly' | 'yearly';

type ProductOption = SelectOption & {
  entityType: SubscriptionEntityType;
  domain?: string;
  solution?: string;
  /** Autocomplete group: domain label, OTHERS, or PACKAGES. */
  domainGroup: string;
  permissionIds: string[];
  msrpKind: MsrpKind | null;
  monthlyMsrp?: number;
  yearlyMsrp?: number;
  msrp?: number;
  price?: number;
  /** Nested products for PACKAGE rows (from catalogue). */
  includedProducts?: PackageIncludedProduct[];
  included?: string[];
  excluded?: string[];
};

const NO_DOMAIN_GROUP = 'OTHERS';
const PACKAGES_GROUP = 'PACKAGES';

const toNumberOrUndefined = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return parsed;
};

const normalizeMsrpKind = (value: unknown): MsrpKind | null => {
  const raw = String(value || '')
    .trim()
    .toUpperCase();
  if (raw === 'MONTHLY') return 'monthly';
  if (raw === 'YEARLY') return 'yearly';
  return null;
};

const matchesBillingCycle = (
  option: ProductOption,
  cycle: SubscriptionType | '',
): boolean => {
  if (cycle !== 'monthly' && cycle !== 'yearly') return false;
  return option.msrpKind === cycle;
};

const sortCatalogueOptions = (products: ProductOption[]): ProductOption[] =>
  [...products].sort((a, b) => {
    const aPkg = a.entityType === 'PACKAGE' || a.domainGroup === PACKAGES_GROUP;
    const bPkg = b.entityType === 'PACKAGE' || b.domainGroup === PACKAGES_GROUP;
    if (aPkg !== bPkg) return aPkg ? 1 : -1;

    const aOther = a.domainGroup === NO_DOMAIN_GROUP;
    const bOther = b.domainGroup === NO_DOMAIN_GROUP;
    if (aOther !== bOther) return aOther ? 1 : -1;

    const domainCmp = a.domainGroup.localeCompare(b.domainGroup, undefined, {
      sensitivity: 'base',
    });
    if (domainCmp !== 0) return domainCmp;
    return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  });

type ProductRow = {
  productId: string;
  licenses: number | '';
};

type CreateSubscriptionFormValues = {
  customerReference: string;
  clientId: string;
  subscriptionType: string;
  items: ProductRow[];
  internalNotes: string;
};

const SUBSCRIPTION_TYPE_OPTIONS: SelectOption[] = [
  { id: 'monthly', label: 'Monthly', value: 'monthly' },
  { id: 'yearly', label: 'Yearly', value: 'yearly' },
];

const defaultItem = (): ProductRow => ({
  productId: '',
  licenses: '',
});

const defaultValues: CreateSubscriptionFormValues = {
  customerReference: '',
  clientId: '',
  subscriptionType: '',
  items: [defaultItem()],
  internalNotes: '',
};

const validationSchema = object({
  customerReference: string().trim().optional(),
  clientId: string().required('Client is required'),
  subscriptionType: string()
    .oneOf(['monthly', 'yearly'], 'Select Monthly or Yearly')
    .required('Subscription type is required'),
  items: array()
    .of(
      object({
        productId: string().required('Product is required'),
        licenses: number()
          .typeError('Enter number of licenses')
          .integer('Licenses must be a whole number')
          .min(1, 'Number of licenses must be greater than 0')
          .max(9999, 'Number of licenses must be less than 10000')
          .required('Number of licenses is required'),
      }),
    )
    .min(1)
    .max(MAX_PRODUCT_ROWS)
    .required(),
  internalNotes: string().trim().optional(),
});

const formatDate = (date: Date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

/** Last calendar day of the month containing `date`. */
const getMonthEndDate = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth() + 1, 0);

/** First day of the month after `date`. */
const getNextMonthStartDate = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth() + 1, 1);

/** Expected end of first billing period shown in preview. */
const getExpectedExpiryDate = (start: Date, cycle: SubscriptionType) => {
  if (cycle === 'yearly') {
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + 1);
    return end;
  }
  // Monthly: first period ends on the last day of the start month.
  return getMonthEndDate(start);
};

/** Next full-period start (renewal) shown in preview. */
const getRenewalDate = (start: Date, cycle: SubscriptionType) => {
  if (cycle === 'yearly') {
    return getExpectedExpiryDate(start, 'yearly');
  }
  return getNextMonthStartDate(start);
};

const mapClientOptions = (clients: any[]): SelectOption[] =>
  (Array.isArray(clients) ? clients : [])
    .map((client, index) => {
      const id = String(client?.id || client?._id || '');
      const label = String(client?.name || client?.organizationName || '').trim();
      if (!id || !label) return null;
      return { id: id || `client-${index}`, label, value: id };
    })
    .filter(Boolean) as SelectOption[];

const mapPermissionIds = (permissions: unknown): string[] =>
  Array.isArray(permissions)
    ? permissions
        .map((permission) => String(permission ?? '').trim())
        .filter(Boolean)
    : [];

const mapCatalogueItemCore = (
  item: any,
  group: any,
  groupIndex: number,
  itemIndex: number,
): ProductOption | null => {
  const id = String(item?.id || item?._id || '').trim();
  const label = String(item?.name || item?.label || '').trim();
  if (!id || !label) return null;

  const groupEntityType =
    String(group?.entityType || '').toUpperCase() === 'PACKAGE'
      ? 'PACKAGE'
      : 'PRODUCT';
  const groupLabel = String(group?.groupLabel || group?.groupKey || '').trim();

  const entityType: SubscriptionEntityType =
    String(item?.entityType || groupEntityType).toUpperCase() === 'PACKAGE'
      ? 'PACKAGE'
      : 'PRODUCT';

  const domainResolved = resolveCatalogDomainLabel(item?.domain, item?.solution);
  const domainGroup =
    entityType === 'PACKAGE'
      ? PACKAGES_GROUP
      : groupLabel || domainResolved || NO_DOMAIN_GROUP;

  const monthlyMsrp = toNumberOrUndefined(item?.monthlyMsrp);
  const yearlyMsrp = toNumberOrUndefined(item?.yearlyMsrp);
  // One MSRP kind per catalogue item (MONTHLY | YEARLY). Prefer explicit msrpKind.
  const msrpKind =
    normalizeMsrpKind(item?.msrpKind) ||
    (monthlyMsrp != null && yearlyMsrp == null
      ? 'monthly'
      : yearlyMsrp != null && monthlyMsrp == null
        ? 'yearly'
        : null);

  const msrp =
    msrpKind === 'yearly'
      ? yearlyMsrp
      : msrpKind === 'monthly'
        ? monthlyMsrp
        : undefined;

  const domainRaw = domainResolved;
  const solutionRaw =
    typeof item?.solution === 'string' ? item.solution.trim() : '';

  return {
    id: id || `catalogue-${groupIndex}-${itemIndex}`,
    label,
    value: id,
    entityType,
    domain: domainRaw || undefined,
    solution: solutionRaw || undefined,
    domainGroup,
    permissionIds: mapPermissionIds(item?.permissions),
    msrpKind,
    monthlyMsrp,
    yearlyMsrp,
    msrp,
    price: msrp,
  };
};

const resolvePackageIncludedProducts = (
  item: any,
  productsById: Map<string, ProductOption>,
): PackageIncludedProduct[] => {
  if (Array.isArray(item?.includedProducts) && item.includedProducts.length > 0) {
    return item.includedProducts
      .map((nested: any) => {
        const id = String(nested?.id || nested?._id || nested?.productId || '').trim();
        const name = String(nested?.name || nested?.label || '').trim();
        if (!id && !name) return null;
        const fromCatalogue = id ? productsById.get(id) : undefined;
        return {
          id: id || fromCatalogue?.value || name,
          name: name || fromCatalogue?.label || id,
          solution:
            (typeof nested?.solution === 'string' && nested.solution.trim()) ||
            fromCatalogue?.solution,
          permissionIds:
            mapPermissionIds(nested?.permissions).length > 0
              ? mapPermissionIds(nested?.permissions)
              : fromCatalogue?.permissionIds || [],
        } as PackageIncludedProduct;
      })
      .filter(Boolean) as PackageIncludedProduct[];
  }

  const ids = Array.isArray(item?.includedProductIds)
    ? item.includedProductIds
    : Array.isArray(item?.productIds)
      ? item.productIds
      : [];

  return ids
    .map((rawId: unknown) => {
      const id = String(rawId || '').trim();
      if (!id) return null;
      const fromCatalogue = productsById.get(id);
      return {
        id,
        name: fromCatalogue?.label || id,
        solution: fromCatalogue?.solution,
        permissionIds: fromCatalogue?.permissionIds || [],
      } as PackageIncludedProduct;
    })
    .filter(Boolean) as PackageIncludedProduct[];
};

const mapCatalogueOptions = (payload: any): ProductOption[] => {
  const root = payload?.data?.data ?? payload?.data ?? payload ?? {};
  const groups = Array.isArray(root?.groups)
    ? root.groups
    : Array.isArray(payload?.groups)
      ? payload.groups
      : [];

  const productsById = new Map<string, ProductOption>();
  groups.forEach((group: any, groupIndex: number) => {
    const items = Array.isArray(group?.items) ? group.items : [];
    items.forEach((item: any, itemIndex: number) => {
      const mapped = mapCatalogueItemCore(item, group, groupIndex, itemIndex);
      if (!mapped || mapped.entityType !== 'PRODUCT') return;
      productsById.set(mapped.value, mapped);
    });
  });

  const mapped: ProductOption[] = [];
  groups.forEach((group: any, groupIndex: number) => {
    const items = Array.isArray(group?.items) ? group.items : [];
    if (items.length === 0) return;

    items.forEach((item: any, itemIndex: number) => {
      const core = mapCatalogueItemCore(item, group, groupIndex, itemIndex);
      if (!core) return;

      if (core.entityType === 'PACKAGE') {
        const includedProducts = resolvePackageIncludedProducts(item, productsById);
        mapped.push({
          ...core,
          includedProducts:
            includedProducts.length > 0 ? includedProducts : undefined,
        });
        return;
      }

      mapped.push(core);
    });
  });

  return sortCatalogueOptions(mapped);
};

const FeatureList = ({
  items,
  tone,
}: {
  items: string[];
  tone: 'included' | 'excluded';
}) => {
  const isIncluded = tone === 'included';
  return (
    <Box
      style={{
        flex: 1,
        backgroundColor: isIncluded ? '#ECFDF5' : '#FEF2F2',
        borderRadius: 8,
        padding: 16,
        minHeight: 120,
      }}>
      {items.length === 0 ? (
        <Typography style={{ fontSize: 13, color: '#9CA3AF' }}>
          {isIncluded ? 'No modules included' : 'No modules excluded'}
        </Typography>
      ) : (
        items.map((item) => (
          <Box
            key={`${tone}-${item}`}
            display="flex"
            alignItems="center"
            style={{ gap: 8, marginBottom: 8 }}>
            {isIncluded ? (
              <CheckCircleOutlineIcon style={{ fontSize: 18, color: '#059669' }} />
            ) : (
              <HighlightOffIcon style={{ fontSize: 18, color: '#DC2626' }} />
            )}
            <Typography style={{ fontSize: 13, color: '#374151' }}>{item}</Typography>
          </Box>
        ))
      )}
    </Box>
  );
};

type PackageProductBreakdown = PackageIncludedProduct & {
  included: string[];
  excluded: string[];
};

const PackageIncludedProductsPanel = ({
  packageName,
  products,
}: {
  packageName: string;
  products: PackageProductBreakdown[];
}) => (
  <Box
    style={{
      backgroundColor: '#F8FAFC',
      borderRadius: 8,
      padding: 16,
      border: '1px solid #E5E7EB',
    }}>
    <Typography
      style={{
        fontSize: 14,
        fontWeight: 700,
        marginBottom: 12,
        color: '#111827',
      }}>
      Products included in {packageName}:
    </Typography>
    {products.length === 0 ? (
      <Typography style={{ fontSize: 13, color: '#9CA3AF' }}>
        No nested products returned for this package
      </Typography>
    ) : (
      products.map((product, index) => {
        const hasModuleBreakdown =
          product.included.length > 0 || product.excluded.length > 0;
        return (
          <Box
            key={product.id}
            style={{
              marginBottom: index === products.length - 1 ? 0 : 16,
              paddingBottom: index === products.length - 1 ? 0 : 16,
              borderBottom:
                index === products.length - 1 ? 'none' : '1px solid #E5E7EB',
            }}>
            <Typography style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
              {index + 1}. {product.name}
            </Typography>
            {hasModuleBreakdown ? (
              <Box
                display="grid"
                gridTemplateColumns="1fr 1fr"
                style={{ gap: 12, marginTop: 10 }}>
                <FeatureList items={product.included} tone="included" />
                <FeatureList items={product.excluded} tone="excluded" />
              </Box>
            ) : null}
          </Box>
        );
      })
    )}
  </Box>
);

const sideCardStyle = (divider: string) => ({
  border: `1px solid ${divider}`,
  borderRadius: 8,
  padding: 20,
  backgroundColor: '#fff',
});

const SearchableSelect = ({
  name,
  outerLabel,
  placeholder,
  options,
  loading = false,
  onSearch,
  disabled = false,
  groupBy,
}: {
  name: string;
  outerLabel?: string;
  placeholder?: string;
  options: SelectOption[];
  loading?: boolean;
  onSearch?: (query: string) => void;
  disabled?: boolean;
  /** When set, options are shown under non-selectable group headers (must be pre-sorted by group). */
  groupBy?: (option: SelectOption) => string;
}) => {
  const theme = useTheme();
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const selected =
          options.find((option) => option.value === field.value) ||
          (field.value
            ? { id: field.value, label: field.value, value: field.value }
            : null);

        return (
          <Box>
            {outerLabel && outerLabel.trim() ? (
              <FormLabel
                style={{
                  paddingBottom: '0.6rem',
                  fontSize: 12,
                  color: '#718096',
                  display: 'block',
                }}>
                {outerLabel}
              </FormLabel>
            ) : null}
            <Autocomplete<SelectOption, false, false, false>
              options={options}
              value={selected}
              loading={loading}
              disabled={disabled}
              getOptionLabel={(option) => option?.label || ''}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(_event, value) => field.onChange(value?.value || '')}
              onInputChange={(_event, value, reason) => {
                if (reason === 'input' && onSearch) {
                  onSearch(value);
                }
              }}
              onBlur={field.onBlur}
              filterOptions={onSearch ? (opts) => opts : undefined}
              noOptionsText={loading ? 'Loading...' : 'No options'}
              {...(groupBy
                ? {
                  groupBy,
                  renderGroup: (params: {
                    key: string | number;
                    group: string;
                    children?: ReactNode;
                  }) => (
                    <li key={params.key}>
                      <Box
                        component="div"
                        style={{
                          position: 'sticky',
                          top: -8,
                          zIndex: 1,
                          padding: '8px 16px',
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          color: '#4B5563',
                          backgroundColor: '#F3F4F6',
                          borderBottom: '1px solid #E5E7EB',
                        }}>
                        {params.group}
                      </Box>
                      <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                    </li>
                  ),
                }
                : {})}
              renderOption={(props, option) => (
                <li
                  {...props}
                  key={option.id}
                  style={{
                    fontSize: 12,
                    borderBottom: '1px solid #E5E7EB',
                    transition: 'background-color 0.15s ease, color 0.15s ease',
                  }}>
                  {option.label}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={placeholder}
                  error={Boolean(error)}
                  helperText={error?.message}
                  inputRef={field.ref}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 50,
                      fontSize: 12,
                      backgroundColor: theme.palette.background.paper,
                      transition: 'background-color 0.2s ease, border-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#fafafa',
                      },
                    },
                  }}
                />
              )}
              ListboxProps={{
                style: { fontSize: 12, maxHeight: 320 },
              }}
            />
          </Box>
        );
      }}
    />
  );
};

const CREATE_SUBSCRIPTION_TABS = [
  { label: 'Quick Create', value: 'quick' },
  { label: 'Guided Setup', value: 'guided' },
] as const;

type CreateSubscriptionTab = (typeof CREATE_SUBSCRIPTION_TABS)[number]['value'];

const isCreateSubscriptionTab = (value: string | undefined | null): value is CreateSubscriptionTab =>
  value === 'quick' || value === 'guided';

const DEFAULT_CREATE_SUBSCRIPTION_TAB: CreateSubscriptionTab = 'quick';

const getCreateSubscriptionHref = (
  tab: CreateSubscriptionTab = DEFAULT_CREATE_SUBSCRIPTION_TAB
) => `/create-subscription/${tab}`;

const GuidedSetupSubscription = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { userData } = useUserProfile();
  const {
    palette: { divider },
  } = theme;

  const [clientOptions, setClientOptions] = useState<SelectOption[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [activePermissions, setActivePermissions] = useState<ActivePermission[]>([]);
  const [createdLicenses, setCreatedLicenses] = useState<SubscriptionLicense[] | null>(
    null,
  );
  const [createdSubscriptionId, setCreatedSubscriptionId] = useState<string | null>(null);
  const [createdLicensesTotal, setCreatedLicensesTotal] = useState(0);
  const [createdLicensesPage, setCreatedLicensesPage] = useState(0);
  const [createdLicensesPageSize, setCreatedLicensesPageSize] = useState(25);
  const [createdLicensesLoading, setCreatedLicensesLoading] = useState(false);
  const productCacheRef = useRef<Map<string, ProductOption>>(new Map());

  const methods = useForm<CreateSubscriptionFormValues>({
    defaultValues,
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema) as any,
  });

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { isSubmitting },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const watchedValues = useWatch({ control });
  const watchedItems = watchedValues?.items ?? [];

  useEffect(() => {
    let cancelled = false;

    const loadClients = async () => {
      const orgId = userData?.organization?.id;
      if (!orgId) {
        setClientOptions([]);
        return;
      }

      setClientsLoading(true);
      try {
        const response = await organizationApi.getByParentId(orgId, 0, 100);
        const clients = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];
        if (!cancelled) {
          setClientOptions(mapClientOptions(clients));
        }
      } catch (_error) {
        if (!cancelled) {
          setClientOptions([]);
          toast.error('Failed to load clients');
        }
      } finally {
        if (!cancelled) {
          setClientsLoading(false);
        }
      }
    };

    loadClients();
    return () => {
      cancelled = true;
    };
  }, [userData?.organization?.id]);

  useEffect(() => {
    let cancelled = false;

    const loadActivePermissions = async () => {
      try {
        // Single request — avoid await-in-loop pagination for eslint/build.
        const response = await permissionsApi.getAll(0, 1000, true);
        const rows = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];
        if (!cancelled) {
          setActivePermissions(mapActivePermissions(rows));
        }
      } catch (_error) {
        if (!cancelled) {
          setActivePermissions([]);
        }
      }
    };

    loadActivePermissions();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchProducts = useMemo(
    () =>
      debounce(async (search: string) => {
        setProductsLoading(true);
        try {
          const response = await pimApi.getProductPackageGroup({
            page: 0,
            pageSize: 100,
            search: search?.trim() || undefined,
            isActive: true,
          });
          const mapped = mapCatalogueOptions(response);
          mapped.forEach((product) => {
            productCacheRef.current.set(product.value, product);
          });
          setProductOptions(mapped);
        } catch (_error) {
          setProductOptions([]);
        } finally {
          setProductsLoading(false);
        }
      }, 400),
    [],
  );

  useEffect(() => {
    fetchProducts('');
    return () => {
      fetchProducts.cancel();
    };
  }, [fetchProducts]);

  const createdByName = useMemo(() => {
    const first = userData?.firstName?.trim() || '';
    const last = userData?.lastName?.trim() || '';
    const full = `${first} ${last}`.trim();
    return full || userData?.email || '—';
  }, [userData]);

  const startDate = useMemo(() => new Date(), []);

  const subscriptionType = watchedValues?.subscriptionType as SubscriptionType | '';
  const expiryDate =
    subscriptionType === 'monthly' || subscriptionType === 'yearly'
      ? getExpectedExpiryDate(startDate, subscriptionType)
      : null;
  const renewalDate =
    subscriptionType === 'monthly' || subscriptionType === 'yearly'
      ? getRenewalDate(startDate, subscriptionType)
      : null;

  // Drop selected products that don't match the chosen MSRP cycle.
  useEffect(() => {
    if (subscriptionType !== 'monthly' && subscriptionType !== 'yearly') return;
    const items = getValues('items') || [];
    items.forEach((item, index) => {
      const productId = item?.productId;
      if (!productId) return;
      const product =
        productCacheRef.current.get(productId) ||
        productOptions.find((option) => option.value === productId);
      if (product && !matchesBillingCycle(product, subscriptionType)) {
        setValue(`items.${index}.productId`, '', { shouldDirty: true, shouldValidate: false });
      }
    });
  }, [subscriptionType, getValues, setValue, productOptions]);

  const resolvedProducts = useMemo(() => {
    const fromCache = Array.from(productCacheRef.current.values());
    const byId = new Map<string, ProductOption>();
    [...fromCache, ...productOptions].forEach((product) => {
      byId.set(product.value, product);
    });
    return sortCatalogueOptions(Array.from(byId.values()));
  }, [productOptions, watchedItems]);

  const productSelectOptions = useMemo(() => {
    const base = resolvedProducts.length ? resolvedProducts : productOptions;
    if (subscriptionType !== 'monthly' && subscriptionType !== 'yearly') {
      return [];
    }
    // Show only catalogue items whose single MSRP kind matches the subscription cycle.
    return base.filter((product) => matchesBillingCycle(product, subscriptionType));
  }, [resolvedProducts, productOptions, subscriptionType]);

  /** Per-row options: hide products already picked on other rows; keep this row's value. */
  const productOptionsForRow = (rowIndex: number) => {
    const taken = new Set(
      (watchedItems || [])
        .map((item, index) =>
          index === rowIndex ? '' : String(item?.productId || '').trim(),
        )
        .filter(Boolean),
    );
    return productSelectOptions.filter((option) => !taken.has(option.value));
  };

  const productSelectDisabled =
    subscriptionType !== 'monthly' && subscriptionType !== 'yearly';

  const previewProductLabel = (() => {
    const names = (watchedItems || [])
      .map((item) => {
        const id = item?.productId || '';
        return (
          resolvedProducts.find((product) => product.value === id)?.label || id
        );
      })
      .filter(Boolean);
    if (names.length === 0) return '—';
    return names.join(', ');
  })();

  const previewLicenses = (() => {
    const total = (watchedItems || []).reduce(
      (sum, item) => sum + (Number(item?.licenses) || 0),
      0,
    );
    return total > 0 ? String(total) : '—';
  })();

  const clientLabel =
    clientOptions.find((client) => client.value === watchedValues?.clientId)?.label ||
    '—';

  const selectedBreakdowns = useMemo(() => {
    const seen = new Set<string>();
    const productCards: ProductOption[] = [];
    const packageCards: Array<{
      id: string;
      label: string;
      products: PackageProductBreakdown[];
    }> = [];

    (watchedItems || []).forEach((item) => {
      const selected = resolvedProducts.find((p) => p.value === item?.productId);
      if (!selected || seen.has(selected.value)) return;
      seen.add(selected.value);

      if (selected.entityType === 'PACKAGE') {
        const products = (selected.includedProducts || []).map((nested) => {
          const { included, excluded } = resolveIncludedExcludedModules(
            activePermissions,
            nested.solution,
            nested.permissionIds,
          );
          return {
            ...nested,
            included,
            excluded,
          };
        });
        packageCards.push({
          id: selected.value,
          label: selected.label,
          products,
        });
        return;
      }

      const { included, excluded } = resolveIncludedExcludedModules(
        activePermissions,
        selected.solution,
        selected.permissionIds,
      );
      if (included.length === 0 && excluded.length === 0) return;

      productCards.push({
        ...selected,
        included,
        excluded,
      });
    });

    return { productCards, packageCards };
  }, [watchedItems, resolvedProducts, activePermissions]);

  const onSubmit = async (values: CreateSubscriptionFormValues) => {
    const cycle = values.subscriptionType as SubscriptionType;
    const payload: CreateSubscriptionPayload = {
      customerReference: values.customerReference?.trim() || undefined,
      subscriptionType: cycle === 'yearly' ? 'YEARLY' : 'MONTHLY',
      clientId: values.clientId,
      startDate: startDate.toISOString(),
      internalNotes: values.internalNotes?.trim() || undefined,
      products: (values.items || []).map((item) => {
        const selected =
          productCacheRef.current.get(item.productId) ||
          productOptions.find((option) => option.value === item.productId);
        return {
          entityType: selected?.entityType === 'PACKAGE' ? 'PACKAGE' : 'PRODUCT',
          productId: item.productId,
          numberOfLicenses: Number(item.licenses),
        };
      }),
    };

    try {
      const response = await subscriptionsApi.createSubscription(payload);
      toast.success('Subscription created');
      const record = parseSubscriptionFromResponse(response);
      const licensesPage = parseLicensesPageFromResponse(response);
      setCreatedSubscriptionId(record?.id ? String(record.id) : null);
      setCreatedLicenses(licensesPage.items);
      setCreatedLicensesTotal(licensesPage.totalRecords);
      setCreatedLicensesPage(0);
      setCreatedLicensesPageSize(licensesPage.pageSize || 25);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to create subscription';
      toast.error(message);
    }
  };

  const handleCancel = () => {
    navigate('/subscriptions');
  };

  if (createdLicenses !== null) {
    const productNames = Object.fromEntries(
      (getValues('items') || []).map((item) => {
        const selected =
          productCacheRef.current.get(item.productId) ||
          productOptions.find((option) => option.value === item.productId);
        return [item.productId, selected?.label || item.productId];
      }),
    );

    return (
      <Paper
        elevation={0}
        style={{
          border: `1px solid ${divider}`,
          borderRadius: 8,
          padding: 24,
          backgroundColor: '#fff',
        }}>
        <Typography
          style={{
            fontSize: 18,
            fontWeight: 700,
            marginBottom: 8,
            color: theme.palette.text.primary,
          }}>
          License keys
        </Typography>
        <Typography
          style={{
            fontSize: 13,
            color: theme.palette.text.secondary,
            marginBottom: 16,
          }}>
          Copy these keys now. They are also stored on the subscription.
        </Typography>
        <SubscriptionLicensesTable
          licenses={createdLicenses}
          productNames={productNames}
          page={createdLicensesPage}
          pageSize={createdLicensesPageSize}
          totalRecords={createdLicensesTotal}
          loading={createdLicensesLoading}
          onPageChange={async (nextPage) => {
            if (!createdSubscriptionId) {
              setCreatedLicensesPage(nextPage);
              return;
            }
            setCreatedLicensesLoading(true);
            setCreatedLicensesPage(nextPage);
            try {
              const response = await subscriptionsApi.getSubscriptionById(
                createdSubscriptionId,
                { page: nextPage + 1, size: createdLicensesPageSize },
              );
              const licensesPage = parseLicensesPageFromResponse(response);
              setCreatedLicenses(licensesPage.items);
              setCreatedLicensesTotal(licensesPage.totalRecords);
            } catch (error: any) {
              toast.error(
                error?.response?.data?.message ||
                  error?.message ||
                  'Failed to load license keys',
              );
            } finally {
              setCreatedLicensesLoading(false);
            }
          }}
          onPageSizeChange={async (size) => {
            setCreatedLicensesPageSize(size);
            setCreatedLicensesPage(0);
            if (!createdSubscriptionId) return;
            setCreatedLicensesLoading(true);
            try {
              const response = await subscriptionsApi.getSubscriptionById(
                createdSubscriptionId,
                { page: 1, size: size },
              );
              const licensesPage = parseLicensesPageFromResponse(response);
              setCreatedLicenses(licensesPage.items);
              setCreatedLicensesTotal(licensesPage.totalRecords);
            } catch (error: any) {
              toast.error(
                error?.response?.data?.message ||
                  error?.message ||
                  'Failed to load license keys',
              );
            } finally {
              setCreatedLicensesLoading(false);
            }
          }}
        />
        <Box display="flex" justifyContent="flex-end" style={{ marginTop: 24 }}>
          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={handleCancel}
            sx={{ minWidth: 120, height: 42, fontWeight: 700 }}>
            Done
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <RHFProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Box
            display="grid"
            gridTemplateColumns="minmax(0, 2fr) minmax(280px, 1fr)"
            style={{ gap: 24, alignItems: 'start' }}>
            <Box>
              <Paper
                elevation={0}
                style={{
                  border: `1px solid ${divider}`,
                  borderRadius: 8,
                  padding: 24,
                  backgroundColor: '#fff',
                }}>
                <Typography
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    marginBottom: 20,
                    color: theme.palette.text.primary,
                  }}>
                  Create Subscription
                </Typography>

                <Box
                  display="grid"
                  gridTemplateColumns="1fr 1fr 40px"
                  style={{ gap: 16, alignItems: 'start' }}>
                  <RHFTextField
                    name="customerReference"
                    outerLabel="Customer Reference"
                    placeholder="Enter customer reference"
                  />
                  <Box />
                  <Box />
                  <SearchableSelect
                    name="clientId"
                    outerLabel="Client"
                    options={clientOptions}
                    loading={clientsLoading}
                    placeholder="Select client"
                  />
                  <RHFCustomSelect
                    name="subscriptionType"
                    outerLabel="Subscription Type"
                    options={SUBSCRIPTION_TYPE_OPTIONS}
                    placeholder="Select type"
                  />
                  <Box />
                </Box>

                <Box style={{ marginTop: 16 }}>
                  {fields.map((field, index) => (
                    <Box
                      key={field.id}
                      display="grid"
                      gridTemplateColumns="1fr 1fr 40px"
                      style={{
                        gap: 16,
                        alignItems: 'start',
                        marginBottom: 16,
                      }}>
                      <SearchableSelect
                        name={`items.${index}.productId`}
                        outerLabel={index === 0 ? 'Product' : undefined}
                        options={productOptionsForRow(index)}
                        loading={productsLoading}
                        disabled={productSelectDisabled || isSubmitting}
                        placeholder={
                          productSelectDisabled
                            ? 'Select subscription type first'
                            : 'Select product'
                        }
                        onSearch={(query) => fetchProducts(query)}
                        groupBy={(option) =>
                          (option as ProductOption).domainGroup || NO_DOMAIN_GROUP
                        }
                      />
                      <RHFTextField
                        name={`items.${index}.licenses`}
                        type="number"
                        outerLabel={index === 0 ? 'Number of Licenses' : undefined}
                        placeholder="0"
                      />
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        style={{
                          height: 50,
                          // Sit beside inputs (below outer labels on the first row).
                          marginTop: index === 0 ? 28 : 0,
                        }}>
                        {fields.length > 1 ? (
                          <IconButton
                            size="small"
                            onClick={() => remove(index)}
                            style={{
                              border: `1px solid ${divider}`,
                              borderRadius: 6,
                            }}>
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                      </Box>
                    </Box>
                  ))}
                </Box>

                <Box
                  display="flex"
                  justifyContent="flex-end"
                  marginTop={0}
                  style={{ paddingRight: 56 }}>
                  <Button
                    type="button"
                    variant="text"
                    color="primary"
                    disabled={
                      productSelectDisabled ||
                      fields.length >= MAX_PRODUCT_ROWS ||
                      isSubmitting
                    }
                    startIcon={<AddIcon />}
                    onClick={() => append(defaultItem())}
                    sx={{ fontWeight: 600, textTransform: 'none' }}>
                    Add
                  </Button>
                </Box>

                <Box style={{ marginTop: 16 }}>
                  <RHFTextField
                    name="internalNotes"
                    outerLabel="Internal Notes"
                    placeholder="Enter notes"
                    multiline
                    rows={4}
                  />
                  <Typography
                    style={{
                      fontSize: 12,
                      color: theme.palette.text.secondary,
                      marginTop: 6,
                    }}>
                    (For Internal Use Only): Sales notes, follow-ups, and other relevant
                    information can be documented here.
                  </Typography>
                </Box>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  style={{ marginTop: 24 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    color="inherit"
                    disabled={isSubmitting}
                    onClick={handleCancel}
                    sx={{ minWidth: 120, height: 42 }}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                    sx={{ minWidth: 120, height: 42, fontWeight: 700 }}>
                    Save
                  </Button>
                </Box>
              </Paper>

              {selectedBreakdowns.productCards.map((product) => (
                <Box key={product.id} style={{ marginTop: 24 }}>
                  <Typography
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      marginBottom: 12,
                      color: theme.palette.text.primary,
                    }}>
                    {product.label}
                  </Typography>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" style={{ gap: 16 }}>
                    <FeatureList items={product.included || []} tone="included" />
                    <FeatureList items={product.excluded || []} tone="excluded" />
                  </Box>
                </Box>
              ))}

              {selectedBreakdowns.packageCards.map((pkg) => (
                <Box key={pkg.id} style={{ marginTop: 24 }}>
                  <Typography
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      marginBottom: 12,
                      color: theme.palette.text.primary,
                    }}>
                    {pkg.label}
                  </Typography>
                  <PackageIncludedProductsPanel
                    packageName={pkg.label}
                    products={pkg.products}
                  />
                </Box>
              ))}
            </Box>

            <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
              <Paper elevation={0} style={sideCardStyle(divider)}>
                <Typography
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    marginBottom: 16,
                    color: theme.palette.text.primary,
                  }}>
                  Subscription Preview
                </Typography>
                {[
                  {
                    label: 'Customer Reference',
                    value: watchedValues?.customerReference?.trim() || '—',
                  },
                  { label: 'Product', value: previewProductLabel },
                  { label: 'Number Of Licenses', value: previewLicenses },
                  { label: 'Client', value: clientLabel },
                  { label: 'Created By', value: createdByName },
                  { label: 'Start Date', value: formatDate(startDate) },
                  {
                    label: 'Expected Expiry Date',
                    value: expiryDate ? formatDate(expiryDate) : '—',
                  },
                  {
                    label: 'Auto Renewal Date',
                    value: renewalDate ? formatDate(renewalDate) : '—',
                  },
                ].map((row) => (
                  <Box
                    key={row.label}
                    display="flex"
                    justifyContent="space-between"
                    style={{ gap: 12, marginBottom: 12 }}>
                    <Typography
                      style={{
                        fontSize: 13,
                        color: theme.palette.text.secondary,
                        flexShrink: 0,
                      }}>
                      {row.label}
                    </Typography>
                    <Typography
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        textAlign: 'right',
                      }}>
                      {row.value}
                    </Typography>
                  </Box>
                ))}
              </Paper>

              {/* Subscription Overview (price/qty table) hidden for now
              <Paper elevation={0} style={sideCardStyle(divider)}>
                ...
              </Paper>
              */}
            </Box>
          </Box>
    </RHFProvider>
  );
};

const CreateSubscription = () => {
  const navigate = useNavigate();
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const requestedTab = isCreateSubscriptionTab(tabParam) ? tabParam : null;
  const activeTab: CreateSubscriptionTab = requestedTab ?? DEFAULT_CREATE_SUBSCRIPTION_TAB;

  useEffect(() => {
    if (tabParam === activeTab) return;
    navigate(getCreateSubscriptionHref(activeTab), { replace: true });
  }, [tabParam, activeTab, navigate]);

  return (
    <>
      <Helmet>
        <title>Mindtime User Portal | Create Subscription</title>
      </Helmet>
      <SubHeader title="Subscriptions" />
      <Box px="30px" pt={3} pb={4}>
        <Tabs
          tabs={[...CREATE_SUBSCRIPTION_TABS]}
          activeTab={activeTab}
          isTransform={false}
          handleTabChange={(_event, value) => {
            const nextTab = String(value ?? '');
            if (!isCreateSubscriptionTab(nextTab)) return;
            navigate(getCreateSubscriptionHref(nextTab));
          }}
        />
        {activeTab === 'guided' ? (
          <GuidedSetupSubscription />
        ) : (
          <QuickCreateSubscription />
        )}
      </Box>
    </>
  );
};

export default CreateSubscription;
